import {HubConnectionBuilder} from '@microsoft/signalr';
import {isEmpty} from 'lodash';

import {URL_HOST} from '@/constants/constant';

// eslint-disable-next-line import/no-cycle
import {buildHub} from './hubBuilder';

const RECONNECTING_TIME_INTERVAL = [0, 1000, 2000, 3000, 4000, 5000];
const SIGNALR_CONNECTION_TIMEOUT = 1200000;
const HUB_NOT_FOUND = 'Hub not found.';

const Hubs = {};

const endPoint = (hubName) => `${URL_HOST}/${hubName}`;

export const startHubPromise = async (
  hubName,
  useToken = false,
  onReconnecting = null,
  onReconnected = null,
  onClose = null,
) =>
  new Promise((resolve, reject) => {
    let hub = null;

    if (useToken) {
      const token = null; // getToken()
      console.debug('startHubPromise url: ', URL_HOST, ' hub name: ', hubName, ' token: ', token);
      hub = new HubConnectionBuilder()
        .withUrl(endPoint(hubName), {
          accessTokenFactory: () => token,
        })
        .withAutomaticReconnect(RECONNECTING_TIME_INTERVAL)
        .build();
    } else {
      console.debug('startHubPromise url: ', URL_HOST, ' hub name: ', hubName);
      hub = new HubConnectionBuilder()
        .withUrl(endPoint(hubName))
        .withAutomaticReconnect(RECONNECTING_TIME_INTERVAL)
        .build();
    }

    hub.serverTimeoutInMilliseconds = SIGNALR_CONNECTION_TIMEOUT;

    hub
      .start()
      .then(() => {
        resolve(hub.connectionId);
        Hubs[hubName] = hub;
      })
      .catch((err) => {
        reject(err);
        delete Hubs[hubName];
      });

    hub.onreconnecting((err) => {
      console.info('hub reconnecting', hubName, err);
      if (onReconnecting) onReconnecting(err);
      Hubs[hubName] = hub;
    });

    hub.onreconnected((connectionId) => {
      console.info('hub reconnected', hubName, connectionId);
      if (onReconnected) onReconnected(connectionId);
      Hubs[hubName] = hub;
    });

    hub.onclose((err) => {
      console.error('hub onclose', err);
      if (onClose) onClose(err);
      delete Hubs[hubName];
    });
  });

export const getHub = (hubName) => {
  if (!isEmpty(Hubs) && Hubs[hubName]) return Hubs[hubName];
  throw HUB_NOT_FOUND;
};

export const stopHubPromise = (hubName) =>
  new Promise((resolve, reject) => {
    console.debug('stopHubPromise hub name: ', hubName);
    const _hub = getHub(hubName);
    _hub
      .stop()
      .then(() => {
        resolve();
        delete Hubs[hubName];
      })
      .catch((err) => {
        reject(err);
      });
  });

const doInvokeAPIPromise = (isSilent, hubName, ApiName, ...ApiParam) =>
  new Promise((resolve, reject) => {
    console.group('[signalR - invoke]');

    if (isSilent) console.debug('invoke', 'hub:', hubName, 'api:', ApiName);
    else console.info('invoke', 'hub:', hubName, 'api:', ApiName, ApiParam.length ? 'params:' : '', ...ApiParam);

    const _hub = getHub(hubName);

    _hub
      .invoke(ApiName, ...ApiParam)
      .then((response) => {
        console.log('response', response);
        console.groupEnd();
        resolve(response);
      })
      .catch((error) => {
        console.error('invoke error', error);
        console.groupEnd();
        if (error === HUB_NOT_FOUND) buildHub(hubName);
        reject(error);
      });
  });

export const invokeAPI = (hubName, ApiName, ...ApiParam) => doInvokeAPIPromise(false, hubName, ApiName, ...ApiParam);

export const silentInvokeAPI = (hubName, ApiName, ...ApiParam) =>
  doInvokeAPIPromise(true, hubName, ApiName, ...ApiParam);

const doSendAPIPromise = (isSilent, hubName, ApiName, ...ApiParam) =>
  new Promise((resolve, reject) => {
    if (isSilent) console.debug('send', 'hub: ', hubName, 'api: ', ApiName);
    else
      console.info('[signalR - send]', 'hub:', hubName, 'api:', ApiName, ApiParam.length ? 'params:' : '', ...ApiParam);

    const _hub = getHub(hubName);

    _hub
      .send(ApiName, ...ApiParam)
      .then(() => resolve())
      .catch((error) => {
        console.error('send error', error);
        if (error === HUB_NOT_FOUND) buildHub(hubName);
        reject(error);
      });
  });

export const sendAPI = (hubName, ApiName, ...ApiParam) => doSendAPIPromise(false, hubName, ApiName, ...ApiParam);

export const silentSendAPI = (hubName, ApiName, ...ApiParam) => doSendAPIPromise(true, hubName, ApiName, ...ApiParam);

export const getHubConnectionId = (hubName) => {
  let result = null;
  if (!isEmpty(Hubs) && Hubs[hubName]) {
    result = Hubs[hubName].connectionId;
  }
  return result;
};

export const getHubsConnected = () => {
  let isHubsConnected = !isEmpty(Hubs);
  if (!isEmpty(Hubs)) {
    // eslint-disable-next-line no-restricted-syntax
    for (const hubName in Hubs) {
      if (Hubs[hubName] && isHubsConnected && Hubs[hubName].connectionState !== 'Connected') isHubsConnected = false;
    }
  }

  return isHubsConnected;
};

export const getHubConnectionState = (hubName) => {
  let result = null;
  if (!isEmpty(Hubs) && Hubs[hubName]) {
    result = Hubs[hubName].state;
  }
  return result;
};

export const subscribeEventListener = (hubName, eventType, action, isSilent = false) =>
  Hubs[hubName].on(eventType, (...args) => {
    try {
      if (!isSilent) console.info('hub:', hubName, 'event on:', eventType, 'data:', args);
      return action(...args);
    } catch (error) {
      console.error('hub:', hubName, 'event on:', eventType, 'data:', args, 'error', error);
      throw error;
    }
  });

export const removeEventListener = (hubName, eventType) =>
  Hubs[hubName].off(eventType, console.info('hub:', hubName, 'event off:', eventType));
